/**
 * Step 5: Line Items - Edit requirement line items
 */
import { useState, useEffect } from 'react';
import { useWizard } from './WizardContext';
import GenerationLock from './GenerationLock';
import { RegeneratedNotice } from './WizardBanners';
import LineItemsEditor from '@/components/LineItemsEditor';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Loader2, AlertTriangle, DollarSign, Lock, RotateCcw, RefreshCw, CalendarIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toastError } from '@/lib/toast';
import api from '@/lib/api';
import { hasAnyLineErrors, countLinesWithErrors, describeLineErrorPointer } from '@/lib/lineItemValidation';
import { isAdvisoryStale } from '@/lib/masterReferenceStaleness';
import { describeRegenerateRefusal } from '@/lib/regenerateRefusal';
import { checkDateRules, appendRuleCodeSuffix, formatRuleCodeSuffix, leadingCode, sortBySeverity, SOFT_ALERT_SEVERITY } from '@/lib/dateValidationRules';
import { summarizeLocationConfirmation } from '@/lib/lineItemLocationChecks';
import { formatDate } from '@/lib/utils';
import SeverityChip from '@/components/SeverityChip';

const RFP_GENERATION_STAGES = [
  'Analyzing requirements...',
  'Applying procurement policy...',
  'Drafting RFP sections...',
  'Building pricing schedule...',
  'Finalizing document...',
];

// Distinct from RFP_GENERATION_STAGES (item 1): the shared `loading` flag
// covers both "Generate RFP" (finalize-line-items + generate-rfp) and
// "Regenerate from answers" (finalize-questions) on this step - showing RFP
// stage text during a line-item-only regeneration was actively misleading
// ("Applying procurement policy...", "Generating RFP..." on a call that
// never touches the RFP). `pendingAction` below picks the right set.
const LINE_REGEN_STAGES = [
  'Reviewing your current answers...',
  'Recomputing line items...',
  'Applying category & UoM rules...',
  'Finalizing line items...',
];

const StepLineItems = () => {
  const {
    loading,
    boqItems,
    boqSource,
    lineItems,
    setLineItems,
    requirementLineItems,
    consistencyValidation,
    getProcurementType,
    getAssessedSpendCategory,
    getShipToLocation,
    getSubmissionDeadlineAnswer,
    setCurrentStep,
    handleFinalizeLineItems,
    handleRestoreAiLines,
    handleFinalizeQuestions,
    selectedCurrency,
    setSelectedCurrency,
    unlockStepForEdit,
    answers,
    regeneratedNotice,
    lineItemErrors,
    postWizardEvent,
    markStepDirty,
    masterReferenceAdvisory,
    categoryActiveResolvable,
    finalLineCount,
  } = useWizard();

  const [currencies, setCurrencies] = useState([]);
  const [locations, setLocations] = useState([]);
  // Date-validation-fixes batch (2026-07-23), items 3/4: per-line soft
  // alerts reported up from LineItemsEditor (single source of truth - the
  // same computation drives its own status column). Replaces the old
  // global "earliest date across all lines" deadline warning entirely -
  // chained/repeated warning dialogs are the alert-noise pattern this
  // batch eliminates; the per-line icons give ambient awareness and this
  // one summary dialog (below) is the single decision point at click time.
  const [lineSoftAlerts, setLineSoftAlerts] = useState([]);
  const [showSoftAlertsSummary, setShowSoftAlertsSummary] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [showForceRegenConfirm, setShowForceRegenConfirm] = useState(false);
  // A REFUSED REGENERATION IS SHOWN WHERE THE BUTTON IS (Ashok ruling,
  // 2026-09-17). The refusal used to leave the page unchanged: the banner
  // re-rendered identically, the overlay flashed, and a 6-second toast told a
  // buyer already on Lines to "update them on the Lines step". This stays
  // beside the button until the next attempt.
  const [regenerateRefusal, setRegenerateRefusal] = useState(null);
  // Multi-location / goods-service-divergence confirm (Issue 15 retest) -
  // set right before the dialog opens, from a fresh summarizeLocationConfirmation
  // computed at Finalize-click time (not reactive) so it always reflects the
  // exact line set about to be finalized.
  const [locationSummary, setLocationSummary] = useState(null);
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  // Merged (client + backend) per-line validation state, reported up from
  // LineItemsEditor (BACKLOG.md Batch 1) - gates the Finalize button.
  const [lineValidation, setLineValidation] = useState([]);
  // Which in-flight action `loading` currently represents - selects the
  // right GenerationLock stage copy (item 1). Set right before the async
  // handler that owns `loading` is invoked.
  const [pendingAction, setPendingAction] = useState(null); // 'lines' | 'rfp' | null

  // Fetch currencies to get display name
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await api.getMasterCurrencies();
        setCurrencies(response.data || []);
      } catch (error) {
        console.error('Failed to fetch currencies:', error);
      }
    };
    fetchCurrencies();
  }, []);

  // Fetch locations for display labels in the multi-location confirm
  // dialog (Issue 15 retest) - mirrors LineItemsEditor's own fetch;
  // duplicated rather than shared per this codebase's established pattern
  // (see lineItemValidation.js header note).
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.getLocations();
        setLocations(response.data.locations || response.data || []);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };
    fetchLocations();
  }, []);

  const getLocationDisplay = (locationId) => {
    const loc = locations.find(l => l.location_id === locationId);
    return loc ? `${loc.location_id} - ${loc.name}` : locationId;
  };

  // Get currency display name
  const getCurrencyDisplay = () => {
    if (!selectedCurrency) return 'Not selected';
    const currency = currencies.find(c => c.currency_code === selectedCurrency);
    if (currency) {
      return `${currency.currency_name} (${currency.currency_code})${currency.symbol ? ' - ' + currency.symbol : ''}`;
    }
    return selectedCurrency;
  };

  const triggerGenerateRfp = () => {
    setPendingAction('rfp');
    handleFinalizeLineItems();
  };

  // Deterministic (no LLM) location checks (Issue 15 retest) - run after
  // hard validation has already passed (the button is disabled otherwise)
  // and after the deadline dialogs above have cleared. Non-blocking: only
  // gates on a confirm dialog, never forbids finalizing.
  const proceedPastLocationCheck = () => {
    const summary = summarizeLocationConfirmation(lineItems, getProcurementType());
    if (summary.needsConfirmation) {
      setLocationSummary(summary);
      setShowLocationConfirm(true);
      return;
    }
    triggerGenerateRfp();
  };

  const confirmLocationsAndProceed = () => {
    setShowLocationConfirm(false);
    if (locationSummary) {
      postWizardEvent('items', 'multi_location_confirmed', {
        locations: locationSummary.groups.map(g => g.location),
        line_count: lineItems.length,
      });
    }
    triggerGenerateRfp();
  };

  const triggerRegenerateFromAnswers = async () => {
    setPendingAction('lines');
    setRegenerateRefusal(null);
    const outcome = await handleFinalizeQuestions(true);
    if (outcome && outcome.ok === false) {
      setRegenerateRefusal(describeRegenerateRefusal(outcome, finalLineCount));
    }
  };

  // Whether "Regenerate from answers" can succeed on this document, as far as
  // the server has said. Since 2026-09-17 the regeneration is exempt from the
  // guard for the LINES it replaces, but not for a stale HEADER (the category
  // on Intake, the ship-to on Answers), which it is built from. The
  // propagation hint must not recommend it where it would be refused.
  const headerReferenceStale = categoryActiveResolvable === false || (
    isAdvisoryStale(masterReferenceAdvisory)
    && masterReferenceAdvisory.stale_references.some((r) => r && (r.line_number === null || r.line_number === undefined))
  );

  // Item 5: routes back to the single authoritative edit point for the
  // submission deadline - Lines only ever shows it read-only.
  const handleEditAtAnswers = () => {
    unlockStepForEdit('answers', answers);
    setCurrentStep(2);
  };

  // Date-validation-fixes batch (2026-07-23): submission-only check
  // (HVPT/SAPT) - doesn't depend on any particular line's dates, so it's
  // evaluated once here (via checkDateRules with no itemType/needBy/start/
  // end) rather than repeated per line. Per-line hard stops (HVND/HVSD/
  // HVES/HVPN/HVPS) are already fully handled by LineItemsEditor's own
  // inline FieldError + the disabled-button gate (hasAnyLineErrors) - this
  // step doesn't re-derive those.
  const submissionDeadlineValue = getSubmissionDeadlineAnswer();
  const submissionCheck = checkDateRules({ submission: submissionDeadlineValue });
  const submissionHardStop = submissionCheck.hardStops[0] || null;
  const submissionSoftAlert = submissionCheck.softAlerts[0] || null;

  // SOFT-ALERT SEVERITY BATCH (2026-07-25, corrected 2026-07-27): the
  // Generate-click summary dialog orders every row (the submission-only
  // concern, if any, plus each affected line) by the shared engine's
  // sortBySeverity - a line's HIGH (SANT/SAST) concern surfaces above a
  // MEDIUM (SAPN/SAPS/SAPT) one, and within MEDIUM the fixed rule-order
  // tiebreak (SAPN/SAPS before SAPT) applies, all defined ONCE in
  // dateValidationRules.js rather than re-derived here.
  const softAlertSummaryRows = sortBySeverity([
    ...(submissionSoftAlert ? [{ kind: 'submission', code: submissionSoftAlert.code }] : []),
    ...lineSoftAlerts.map((entry) => ({
      kind: 'line',
      entry,
      code: leadingCode(entry.alerts.map((a) => a.code)),
    })),
  ], 'code');

  const handleGenerateRfpClick = () => {
    // Item 2/backend gate mirrors this same submission-only hard check;
    // this is the client-side first line of defense against a deadline
    // that slid into the past while this procurement sat half-finished.
    if (submissionHardStop) {
      toastError(appendRuleCodeSuffix(submissionHardStop.message, submissionHardStop.code));
      return;
    }

    // Item 4: ONE summary dialog - not chained per-warning-type modals.
    // Per-line soft alerts (SANT/SAST/SAPN/SAPS) and the submission-only
    // soft alert (SAPT) are both folded in here; the per-line amber icons
    // already gave ambient awareness, this is the single decision point.
    if (lineSoftAlerts.length > 0 || submissionSoftAlert) {
      setShowSoftAlertsSummary(true);
      return;
    }

    proceedPastLocationCheck();
  };

  return (
    <div className="relative">
      <GenerationLock
        active={loading}
        stages={pendingAction === 'lines' ? LINE_REGEN_STAGES : RFP_GENERATION_STAGES}
        stageDurationMs={8000}
      />
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-zinc-900 mb-2">
          Review & Edit Requirement Line Items
        </h2>
        <p className="text-zinc-600">
          Review and edit the line items for vendor pricing.
          {boqItems.length > 0 && (
            <span className="ml-2 text-green-600 font-medium">
              ({boqItems.length} items pre-filled from BOQ)
            </span>
          )}
        </p>
      </div>

      {regeneratedNotice?.stepId === 'items' && (
        <RegeneratedNotice causeLabel={regeneratedNotice.causeLabel} />
      )}

      {/* Item 5 (consolidated fix batch, 2026-07-24): currency and submission
          deadline are both read-only reference values once known, so they
          sit side-by-side in one row (currency left, deadline right) to
          reclaim vertical space above the lines table - stacks back to
          vertical on narrow screens. */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
      {/* Currency Display - locked once known; editable inline when missing
          (Issue 14/BLOCKER) - resuming a procurement saved before
          rfp_currency was persisted (or any other path that left it unset)
          must not force a trip back to Answers just to unblock Generate RFP. */}
      {selectedCurrency ? (
        <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-blue-900">RFP Currency:</span>
                <span className="text-sm text-blue-800 font-medium">{getCurrencyDisplay()}</span>
                <Lock className="w-3 h-3 text-blue-400" />
              </div>
              <p className="text-xs text-blue-600 mt-1">
                Currency was set in the Answers step. All vendor quotations will be in this currency.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 bg-amber-50 border border-amber-300 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <span className="text-sm font-semibold text-amber-900 block mb-2">
                RFP Currency not set — select one to continue
              </span>
              <Select
                value={selectedCurrency || ''}
                onValueChange={(value) => {
                  setSelectedCurrency(value);
                  markStepDirty('items');
                }}
              >
                <SelectTrigger className="w-full max-w-md bg-white" data-testid="lines-currency-select">
                  <SelectValue placeholder="Select RFP currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((currency) => (
                    <SelectItem key={currency.currency_code} value={currency.currency_code}>
                      {currency.currency_code} - {currency.currency_name}
                      {currency.symbol && ` (${currency.symbol})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-amber-700 mt-1">
                Needed for vendor quotations. Normally captured in the Answers step - this
                procurement doesn't have one on record yet.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Item 5: submission deadline READ-ONLY reference - single
          authoritative edit point is Answers, never directly editable here. */}
      <div className={`flex-1 rounded-lg p-4 ${submissionHardStop ? 'bg-red-50 border border-red-300' : 'bg-blue-50 border border-blue-200'}`}>
        <div className="flex items-center gap-3">
          <CalendarIcon className={`w-5 h-5 ${submissionHardStop ? 'text-red-600' : 'text-blue-600'}`} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${submissionHardStop ? 'text-red-900' : 'text-blue-900'}`}>
                Submission Deadline:
              </span>
              <span className={`text-sm font-medium ${submissionHardStop ? 'text-red-800' : 'text-blue-800'}`} data-testid="submission-deadline-reference">
                {submissionDeadlineValue ? formatDate(submissionDeadlineValue) : 'Not set'}
              </span>
              <Lock className={`w-3 h-3 ${submissionHardStop ? 'text-red-400' : 'text-blue-400'}`} />
            </div>
            {submissionHardStop ? (
              <p className="text-xs text-red-600 mt-1" data-testid="submission-deadline-hard-stop">
                {appendRuleCodeSuffix(submissionHardStop.message, submissionHardStop.code)}
              </p>
            ) : submissionSoftAlert ? (
              <p className="text-xs text-amber-600 mt-1">{appendRuleCodeSuffix(submissionSoftAlert.message, submissionSoftAlert.code)}</p>
            ) : (
              <p className="text-xs text-blue-600 mt-1">
                Set in the Answers step. Applies to every line item below.
              </p>
            )}
          </div>
          <Button
            variant="link"
            size="sm"
            onClick={handleEditAtAnswers}
            className="text-xs"
            data-testid="edit-submission-at-answers-link"
          >
            Edit at Answers
          </Button>
        </div>
      </div>
      </div>

      {/* Consistency Validation Warnings */}
      {consistencyValidation && !consistencyValidation.is_consistent && consistencyValidation.issues?.length > 0 && (
        <div className="mb-6 border border-amber-300 bg-amber-50 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-amber-100 border-b border-amber-200 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-amber-900">Consistency Check - Issues Found</h3>
          </div>
          <div className="p-4 space-y-3">
            {consistencyValidation.issues.map((issue, idx) => (
              <div 
                key={`consistency-issue-${issue.field || idx}-${issue.severity}`} 
                className={`p-3 rounded-lg ${
                  issue.severity === 'error' 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-yellow-50 border border-yellow-200'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                    issue.severity === 'error' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {issue.severity?.toUpperCase()}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-zinc-900">{issue.message}</p>
                    {issue.expected && issue.actual && (
                      <div className="mt-1 text-sm text-zinc-600">
                        <span className="text-red-600">Found: {issue.actual}</span>
                        {' | '}
                        <span className="text-green-600">Expected: {issue.expected}</span>
                      </div>
                    )}
                    {issue.suggestion && (
                      <p className="mt-1 text-sm text-blue-600">
                        <strong>Suggestion:</strong> {issue.suggestion}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <p className="text-sm text-amber-700 mt-2">
              Please correct these issues in the line items below before proceeding.
            </p>
          </div>
        </div>
      )}
      
      {/* Lines step extras (spec P2 §4.4) - independent of staleness,
          always available regardless of whether anything upstream changed. */}
      {regenerateRefusal && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2 text-sm text-red-900"
          role="alert"
          data-testid="regenerate-refusal"
        >
          <p className="font-medium">&ldquo;Regenerate from answers&rdquo; could not run.</p>
          <p className="mt-1 text-red-800">{regenerateRefusal}</p>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowRestoreConfirm(true)}
          disabled={loading || requirementLineItems.length === 0}
          className="border-zinc-200 text-xs"
          data-testid="restore-ai-lines-btn"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore AI-generated lines
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowForceRegenConfirm(true)}
          disabled={loading}
          className="border-zinc-200 text-xs"
          data-testid="regenerate-from-answers-btn"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate from answers
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 p-6 mb-6">
        <LineItemsEditor
          lineItems={lineItems}
          setLineItems={setLineItems}
          procurementType={getProcurementType()}
          shipToLocation={getShipToLocation()}
          submissionDeadline={getSubmissionDeadlineAnswer()}
          serverErrors={lineItemErrors}
          onValidationChange={setLineValidation}
          onSoftAlertsChange={setLineSoftAlerts}
          assessedCategoryCode={getAssessedSpendCategory()}
          onDirty={() => markStepDirty('items')}
          boqSource={boqSource}
          showPropagationHint
          regenerateAvailable={!headerReferenceStale}
        />
      </div>

      <div className="flex flex-col items-end gap-2">
        <div className="flex justify-between w-full">
          <Button variant="outline" onClick={() => setCurrentStep(3)} disabled={loading} className="border-zinc-200">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button
            onClick={handleGenerateRfpClick}
            disabled={loading || lineItems.length === 0 || !selectedCurrency || hasAnyLineErrors(lineValidation) || Boolean(submissionHardStop)}
            className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-6"
            data-testid="generate-rfp-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            {loading ? (pendingAction === 'lines' ? 'Regenerating line items...' : 'Generating RFP...') : 'Generate RFP'}
          </Button>
        </div>
        {/* Explains a disabled Generate RFP rather than leaving it silently
            dead (Issue 14/BLOCKER, extended to line-item validation in
            BACKLOG.md Batch 1) - not shown while loading, since the
            overlay already explains that state. */}
        {!loading && lineItems.length > 0 && selectedCurrency && !hasAnyLineErrors(lineValidation) && submissionHardStop && (
          <p className="text-xs text-red-600 font-medium" data-testid="generate-rfp-disabled-reason">
            {appendRuleCodeSuffix(submissionHardStop.message, submissionHardStop.code)}
          </p>
        )}
        {!loading && lineItems.length > 0 && !selectedCurrency && (
          <p className="text-xs text-amber-600" data-testid="generate-rfp-disabled-reason">
            Select an RFP currency above to continue.
          </p>
        )}
        {/* A POINTER, NOT A SECOND LIST (Ashok ruling, 2026-09-17). It sits
            beside the disabled button because that is where the buyer is when
            they find they cannot proceed (ruling D, 2026-09-03). It counts
            EVERY line with an error - a blank quantity as much as a retired
            code - and points at the fields, where each error already renders
            inline; stale references are also in the banner, but a blank
            quantity is not. Shown whenever any line has an error, whatever
            else is missing. */}
        {!loading && lineItems.length > 0 && hasAnyLineErrors(lineValidation) && (
          <p className="text-xs text-red-600 font-medium text-right" data-testid="generate-rfp-lines-pointer">
            {describeLineErrorPointer(countLinesWithErrors(lineValidation))}
          </p>
        )}
      </div>

      {/* Item 4: ONE summary dialog for every soft alert at click time -
          not chained per-warning-type modals. Lists the submission-only
          concern (if any) plus every affected line's consolidated message. */}
      <Dialog open={showSoftAlertsSummary} onOpenChange={setShowSoftAlertsSummary}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Timing Concerns Found
            </DialogTitle>
            <DialogDescription>
              {lineSoftAlerts.length > 0
                ? `${lineSoftAlerts.length} line${lineSoftAlerts.length === 1 ? '' : 's'} may not leave vendors enough time to respond and deliver. Review before proceeding.`
                : 'The submission deadline is close. Review before proceeding.'}
            </DialogDescription>
          </DialogHeader>
          {/* Item 2: this IS a dialog, so the rule-code suffix renders as a
              separately-styled muted span - built directly from `.code`/
              `alerts`, not from the already-plain-suffixed
              consolidatedMessage (that string is for LineItemsEditor's
              inline amber panel, which isn't a dialog).
              SOFT-ALERT SEVERITY BATCH (2026-07-25): rows ordered
              most-severe-first via softAlertSummaryRows (computed above),
              each carrying its own severity chip. */}
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="soft-alerts-summary-list">
            {softAlertSummaryRows.map((row) => {
              if (row.kind === 'submission') {
                return (
                  <div key="submission" className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
                    <SeverityChip severity={SOFT_ALERT_SEVERITY[row.code]} className="mr-1.5 align-middle" />
                    {submissionSoftAlert.message}{' '}
                    <span className="text-amber-400 text-xs">{formatRuleCodeSuffix(submissionSoftAlert.code)}</span>
                  </div>
                );
              }
              const { entry } = row;
              const item = lineItems[entry.lineIndex];
              const lineNumber = item?.line_number ?? entry.lineIndex + 1;
              return (
                <div key={entry.lineIndex} className="border border-amber-200 bg-amber-50 rounded-md p-3 text-sm text-amber-800">
                  <SeverityChip severity={SOFT_ALERT_SEVERITY[row.code]} className="mr-1.5 align-middle" />
                  <span className="font-semibold">Line {lineNumber}:</span>{' '}
                  {/* Generate-summary polish (2026-07-27): each contributing
                      rule's sentence starts on its own line within this
                      entry - joined with '\n' + whitespace-pre-line, not a
                      space, so a two-alert line doesn't read as a run-on
                      paragraph. Codes stay combined in one trailing bracket. */}
                  <span className="whitespace-pre-line align-top">{entry.alerts.map((a) => a.message).join('\n')}</span>{' '}
                  <span className="text-amber-400 text-xs">{formatRuleCodeSuffix(entry.alerts.map((a) => a.code))}</span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowSoftAlertsSummary(false)}>
              Go Back and Review
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { setShowSoftAlertsSummary(false); proceedPastLocationCheck(); }}
              data-testid="confirm-soft-alerts-summary-btn"
            >
              Proceed Anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Multi-location / goods-service-divergence confirm (Issue 15
          retest) - non-blocking: Cancel returns to editing, Confirm
          proceeds and logs a wizard_event. Combined into one dialog even
          when both rules trigger (needsConfirmation implies the
          multi-location list is always shown; the divergence callout is
          appended additionally when it also applies). */}
      <Dialog open={showLocationConfirm} onOpenChange={setShowLocationConfirm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm Multiple Delivery Locations</DialogTitle>
            <DialogDescription>
              This RFP delivers to {locationSummary?.groups?.length || 0} different locations. Please confirm this is intentional before proceeding.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-72 overflow-y-auto" data-testid="location-confirm-groups">
            {locationSummary?.groups?.map((group) => (
              <div key={group.location} className="border border-zinc-200 rounded-md p-3">
                <div className="text-sm font-semibold text-zinc-900">
                  {getLocationDisplay(group.location)} ({group.count} line{group.count === 1 ? '' : 's'})
                </div>
                <ul className="mt-1 text-xs text-zinc-600 list-disc list-inside">
                  {group.lines.map((line, idx) => (
                    <li key={`${group.location}-${line.line_number ?? idx}`}>{line.description}</li>
                  ))}
                </ul>
              </div>
            ))}
            {locationSummary?.divergence?.diverges && (
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3 text-sm text-amber-800" data-testid="location-divergence-flag">
                Services location{locationSummary.divergence.serviceLocations.length === 1 ? '' : 's'} (
                {locationSummary.divergence.serviceLocations.map(getLocationDisplay).join(', ')}) differs from goods
                delivery location{locationSummary.divergence.goodsLocations.length === 1 ? '' : 's'} (
                {locationSummary.divergence.goodsLocations.map(getLocationDisplay).join(', ')}).
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowLocationConfirm(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              onClick={confirmLocationsAndProceed}
              data-testid="confirm-multi-location-btn"
            >
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Restore AI-generated lines?</DialogTitle>
            <DialogDescription>
              This replaces your current line items with the originally AI-generated set - no new
              generation, no credit used. Your current edits will be lost.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowRestoreConfirm(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              onClick={() => { setShowRestoreConfirm(false); handleRestoreAiLines(); }}
              data-testid="confirm-restore-ai-lines-btn"
            >
              Restore
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showForceRegenConfirm} onOpenChange={setShowForceRegenConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Regenerate line items from answers?</DialogTitle>
            <DialogDescription>
              This re-runs generation from your current answers even if nothing has changed, and
              replaces your current line items with the fresh result. Your current edits will be lost.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowForceRegenConfirm(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              onClick={() => { setShowForceRegenConfirm(false); triggerRegenerateFromAnswers(); }}
              data-testid="confirm-regenerate-from-answers-btn"
            >
              Regenerate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StepLineItems;
